import { combineEpics, ofType } from 'redux-observable';
import { pluck, switchMap } from 'rxjs/operators';

import { Epic } from 'src/config/rootEpic';
import { INITIAL_DATA_FETCH_READY } from 'src/shared/actions/sharedActions';
import * as weekFourActions from '../weekFour/actions/weekFourActions';
import * as weekOneActions from '../weekOne/actions/weekOneActions';
import * as weekSharedActions from '../weekShared/actions/weekSharedActions';
import * as weekThreeActions from '../weekThree/actions/weekThreeActions';
import * as weekTwoActions from '../weekTwo/actions/weekTwoActions';

import { ProgramEnum } from '../../models/programs';
import { getProgramWeeksDataByProgramId } from '../../selectors/programsSelectors';
import { CPProgramWeek } from '../models/cpProgram';
import { CPProgramService } from '../services/cpProgramServices';
import { ContentAnswer, SelectedAnswer } from '../weekFour/models/weekFour';
import { MindfulnessImprovementsTopics } from '../weekOne/models/weekOne';
import { getCorePillarsRating } from '../weekShared/selectors/weekSharedSelectors';
import {
    ActivityData,
    LivingAccordingToValues,
    PlannedActivity,
} from '../weekThree/models/weekThree';
import { ValuesAssessmentDomain } from '../weekTwo/models/weekTwo';

export function cpProgramEpicFactory(cpProgramService: CPProgramService): Epic {
    const fetchCorePillarsRatingEpic: Epic = action$ =>
        action$.pipe(
            ofType(INITIAL_DATA_FETCH_READY),
            switchMap(() =>
                cpProgramService
                    .fetchCorePillarsRating()
                    .then(weekSharedActions.fetchCorePillarsRatingSuccess)
                    .catch(weekSharedActions.fetchCorePillarsRatingFail),
            ),
        );

    const submitCorePillarsRatingFormEpic: Epic = (action$, store) =>
        action$.pipe(
            ofType(weekSharedActions.SUBMIT_CORE_PILLARS_RATING_FORM_REQUESTED),
            switchMap(() => {
                const state = store.getState();
                const weeks = getProgramWeeksDataByProgramId(
                    state,
                    ProgramEnum.CP,
                ) as CPProgramWeek[];
                const rating = getCorePillarsRating(state);

                return cpProgramService
                    .submitCorePillarsRatingForm(weeks, rating)
                    .then(weekSharedActions.submitCorePillarsRatingFormSuccess)
                    .catch(weekSharedActions.submitCorePillarsRatingFormFail);
            }),
        );

    const submitMindfulnessImprovementsDataEpic: Epic = action$ =>
        action$.pipe(
            ofType(weekOneActions.SUBMIT_MINDFULNESS_IMPROVEMENTS_DATA_REQUESTED),
            pluck('payload'),
            switchMap((data: MindfulnessImprovementsTopics) =>
                cpProgramService
                    .submitMindfulnessImprovementsData(data)
                    .then(weekOneActions.submitMindfulnessImprovementsDataSuccess)
                    .catch(weekOneActions.submitMindfulnessImprovementsDataFail),
            ),
        );

    const fetchMindfulnessImprovementsDataEpic: Epic = action$ =>
        action$.pipe(
            ofType(INITIAL_DATA_FETCH_READY),
            switchMap(() =>
                cpProgramService
                    .fetchMindfulnessImprovementsData()
                    .then(weekOneActions.fetchMindfulnessImprovementsDataSuccess)
                    .catch(weekOneActions.fetchMindfulnessImprovementsDataFail),
            ),
        );

    const submitHomeworkConfidenceRatingRequestEpic: Epic = action$ =>
        action$.pipe(
            ofType(weekTwoActions.SUBMIT_HOMEWORK_CONFIDENCE_RATING_REQUESTED),
            pluck('payload'),
            switchMap((homeworkConfidenceRating: number) =>
                cpProgramService
                    .submitHomeworkConfidenceRating(homeworkConfidenceRating)
                    .then(weekTwoActions.submitHomeworkConfidenceRatingSuccess)
                    .catch(weekTwoActions.submitHomeworkConfidenceRatingFail),
            ),
        );

    const submitValuesAssessmentsDataEpic: Epic = action$ =>
        action$.pipe(
            ofType(weekTwoActions.SUBMIT_VALUES_ASSESSMENTS_DATA_REQUESTED),
            pluck('payload'),
            switchMap((assessments: ValuesAssessmentDomain[]) =>
                cpProgramService
                    .submitValuesAssessmentsData(assessments)
                    .then(weekTwoActions.submitValuesAssessmentsDataSuccess)
                    .catch(weekTwoActions.submitValuesAssessmentsDataFail),
            ),
        );

    const fetchValuesAssessmentsDataEpic: Epic = action$ =>
        action$.pipe(
            ofType(INITIAL_DATA_FETCH_READY),
            switchMap(() =>
                cpProgramService
                    .fetchValuesAssessmentsData()
                    .then(weekTwoActions.fetchValuesAssessmentsDataSuccess)
                    .catch(weekTwoActions.fetchValuesAssessmentsDataFail),
            ),
        );

    const submitActivitiesSelectionDataEpic: Epic = action$ =>
        action$.pipe(
            ofType(weekThreeActions.SUBMIT_ACTIVITIES_DATA_REQUESTED),
            pluck('payload'),
            switchMap((selectedActivities: PlannedActivity[]) =>
                cpProgramService
                    .submitActivitiesSelectionData(selectedActivities)
                    .then(weekThreeActions.submitActivitiesDataSuccess)
                    .catch(weekThreeActions.submitActivitiesDataFail),
            ),
        );

    const fetchActivitiesSelectionDataEpic: Epic = action$ =>
        action$.pipe(
            ofType(INITIAL_DATA_FETCH_READY),
            switchMap(() =>
                cpProgramService
                    .fetchActivitiesSelectionData()
                    .then(weekThreeActions.fetchActivitiesDataSuccess)
                    .catch(weekThreeActions.fetchActivitiesDataFail),
            ),
        );

    const fetchPlannedActivitiesEpic: Epic = action$ =>
        action$.pipe(
            ofType(weekThreeActions.FETCH_ACTIVITIES_DATA_SUCCEEDED),
            switchMap(() =>
                cpProgramService
                    .fetchPlannedActivities()
                    .then(weekThreeActions.fetchPlannedActivitiesSuccess)
                    .catch(weekThreeActions.fetchPlannedActivitiesFail),
            ),
        );

    const submitLivingValuesDataEpic: Epic = action$ =>
        action$.pipe(
            ofType(weekThreeActions.SUBMIT_LIVING_VALUES_DATA_REQUESTED),
            pluck('payload'),
            switchMap((data: LivingAccordingToValues) =>
                cpProgramService
                    .submitLivingValuesData(data)
                    .then(weekThreeActions.submitLivingValuesDataSuccess)
                    .catch(weekThreeActions.submitLivingValuesDataFail),
            ),
        );

    const fetchFlexibilityExerciseQuestionsEpic: Epic = action$ =>
        action$.pipe(
            ofType(INITIAL_DATA_FETCH_READY),
            switchMap(() =>
                cpProgramService
                    .fetchFlexibilityExerciseQuestions()
                    .then(weekFourActions.fetchFlexibilityExerciseQuestionsSuccess)
                    .catch(weekFourActions.fetchFlexibilityExerciseQuestionsFail),
            ),
        );

    const submitFlexibilityExerciseAnswersEpic: Epic = action$ =>
        action$.pipe(
            ofType(weekFourActions.SUBMIT_FLEXIBILITY_EXERCISE_ANSWERS_REQUESTED),
            pluck('payload'),
            switchMap((answers: SelectedAnswer[]) =>
                cpProgramService
                    .submitFlexibilityExerciseAnswers(answers)
                    .then(weekFourActions.submitFlexibilityExerciseAnswersSuccess)
                    .catch(weekFourActions.submitFlexibilityExerciseAnswersFail),
            ),
        );

    const fetchFlexibilityExerciseAnswersEpic: Epic = action$ =>
        action$.pipe(
            ofType(weekFourActions.FETCH_FLEXIBILITY_EXERCISE_QUESTIONS_SUCCEEDED),
            switchMap(() =>
                cpProgramService
                    .fetchFlexibilityExerciseAnswers()
                    .then(weekFourActions.fetchFlexibilityExerciseAnswersSuccess)
                    .catch(weekFourActions.fetchFlexibilityExerciseAnswersFail),
            ),
        );

    const fetchFlexibilityPracticeQuestionEpic: Epic = action$ =>
        action$.pipe(
            ofType(INITIAL_DATA_FETCH_READY),
            switchMap(() =>
                cpProgramService
                    .fetchFlexibilityPracticeQuestion()
                    .then(weekFourActions.fetchFlexibilityPracticeQuestionSuccess)
                    .catch(weekFourActions.fetchFlexibilityPracticeQuestionFail),
            ),
        );

    const submitFlexibilityPracticeAnswerEpic: Epic = action$ =>
        action$.pipe(
            ofType(weekFourActions.SUBMIT_FLEXIBILITY_PRACTICE_ANSWER_REQUESTED),
            pluck('payload'),
            switchMap((answer: ContentAnswer) =>
                cpProgramService
                    .submitFlexibilityPracticeAnswer(answer)
                    .then(weekFourActions.submitFlexibilityPracticeAnswerSuccess)
                    .catch(weekFourActions.submitFlexibilityPracticeAnswerFail),
            ),
        );

    const fetchFlexibilityPracticeAnswerEpic: Epic = action$ =>
        action$.pipe(
            ofType(weekFourActions.FETCH_FLEXIBILITY_PRACTICE_QUESTION_SUCCEEDED),
            switchMap(() =>
                cpProgramService
                    .fetchFlexibilityPracticeAnswer()
                    .then(weekFourActions.fetchFlexibilityPracticeAnswerSuccess)
                    .catch(weekFourActions.fetchFlexibilityPracticeAnswerFail),
            ),
        );

    const fetchFlexibilityPlanDataEpic: Epic = action$ =>
        action$.pipe(
            ofType(INITIAL_DATA_FETCH_READY),
            switchMap(() =>
                cpProgramService
                    .fetchFlexibilityPlanQuestion()
                    .then(weekFourActions.fetchFlexibilityPlanDataSuccess)
                    .catch(weekFourActions.fetchFlexibilityPlanDataFail),
            ),
        );

    const submitFlexibilityPlanDataEpic: Epic = action$ =>
        action$.pipe(
            ofType(weekFourActions.SUBMIT_FLEXIBILITY_PLAN_DATA_REQUESTED),
            pluck('payload'),
            switchMap((planData: SelectedAnswer[]) =>
                cpProgramService
                    .submitFlexibilityPlanData(planData)
                    .then(weekFourActions.submitFlexibilityPlanDataSuccess)
                    .catch(weekFourActions.submitFlexibilityPlanDataFail),
            ),
        );

    const fetchFlexibilityPlanAnswersEpic: Epic = action$ =>
        action$.pipe(
            ofType(weekFourActions.FETCH_FLEXIBILITY_PLAN_DATA_SUCCEEDED),
            switchMap(() =>
                cpProgramService
                    .fetchFlexibilityPlanAnswers()
                    .then(weekFourActions.fetchFlexibilityPlanAnswersSuccess)
                    .catch(weekFourActions.fetchFlexibilityPlanAnswersFail),
            ),
        );

    const submitFlexibilityHomeworkDataEpic: Epic = action$ =>
        action$.pipe(
            ofType(weekFourActions.SUBMIT_FLEXIBILITY_HOMEWORK_DATA_REQUESTED),
            pluck('payload'),
            switchMap((flexibilityHomework: ActivityData[]) =>
                cpProgramService
                    .submitFlexibilityHomeworkData(flexibilityHomework)
                    .then(weekFourActions.submitFlexibilityHomeworkDataSuccess)
                    .catch(weekFourActions.submitFlexibilityHomeworkDataFail),
            ),
        );

    const fetchFlexibilityHomeworkDataEpic: Epic = action$ =>
        action$.pipe(
            ofType(INITIAL_DATA_FETCH_READY),
            switchMap(() =>
                cpProgramService
                    .fetchFlexibilityHomeworkData()
                    .then(weekFourActions.fetchFlexibilityHomeworkDataSuccess)
                    .catch(weekFourActions.fetchFlexibilityHomeworkDataFail),
            ),
        );

    return combineEpics(
        fetchCorePillarsRatingEpic,
        submitCorePillarsRatingFormEpic,
        submitMindfulnessImprovementsDataEpic,
        fetchMindfulnessImprovementsDataEpic,
        submitHomeworkConfidenceRatingRequestEpic,
        submitValuesAssessmentsDataEpic,
        fetchValuesAssessmentsDataEpic,
        submitActivitiesSelectionDataEpic,
        fetchActivitiesSelectionDataEpic,
        fetchPlannedActivitiesEpic,
        submitLivingValuesDataEpic,
        submitFlexibilityExerciseAnswersEpic,
        fetchFlexibilityExerciseQuestionsEpic,
        submitFlexibilityPracticeAnswerEpic,
        fetchFlexibilityPracticeQuestionEpic,
        submitFlexibilityPlanDataEpic,
        fetchFlexibilityPlanDataEpic,
        fetchFlexibilityPlanAnswersEpic,
        submitFlexibilityHomeworkDataEpic,
        fetchFlexibilityPracticeAnswerEpic,
        fetchFlexibilityExerciseAnswersEpic,
        fetchFlexibilityHomeworkDataEpic,
    );
}
